import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import {
  LENGTH_DEFAULT_RECT,
  WIDTH_DEFAULT_RECT,
} from 'src/app/services/helpers/workbench-helpers/workbench-const-helper';
import { WorkbenchConfiguratorItem } from '../../configurator/applications/workbench-configurator-item';
import { WorkbenchService } from '../../../../services/configurators-2d/applications/workbench.service';
import { Shape } from '../../../../models/configurators-2d/applications/workbench/shape';
import {
  ConfigurationPlan,
  isPlanDimensionsValid,
} from '../../../../models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { ShapeTypeEnum } from 'src/app/enum/workbench/shape-type.enum';
import { IWorkbenchPreview } from '../preview/i-workbench-preview';
import { Configurators2DComponent } from '../../configurators-2d.component';
import { IBaseComponent } from '../../configurator/i-base-component';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { HBMMedia, HBMMedias } from 'src/app/models/configurators-2d/applications/kitchen/hbm-media';
import { Subscription } from 'rxjs';
import { TunnelDataStore } from '../../../../stores/tunnel/tunnel-data.store';
import { CONFIG_CONTEXT } from '../../../../enum/config-context.enum';
import { KitchenConfigurationData } from '../../../../stores/tunnel/model/configuration-data/kitchen-configuration-data';
import { TunnelNavigationStore } from '../../../../stores/tunnel/tunnel-navigation.store';

@Component({
  selector: 'ng-workbench-plan-groups',
  templateUrl: './plan-groups.component.html',
})
export class WorkbenchPlanGroupsComponent
  extends WorkbenchConfiguratorItem
  implements OnInit, OnDestroy, IBaseComponent {
  @Output() onSectionSelected: EventEmitter<boolean> = new EventEmitter();
  shapes: Shape[];
  // Plan visible
  displayedPlanIndex: number;
  // Sous menu visible
  subStep: number = 1;

  preview: IWorkbenchPreview;

  kitchenMedias: HBMMedias = new HBMMedias();
  subscriptions: Subscription[] = [];

  kitchenCarouselMedias: HBMMedia[] = [];
  kitchenPlanVisible: boolean = false;
  kitchenPlanDisplayed: number = 0;

  private mainContext: CONFIG_CONTEXT = undefined;

  readonly faExclamationTriangle = faExclamationTriangle;

  constructor(
    public workbenchService: WorkbenchService,
    configurators2DComponent: Configurators2DComponent,
    private readonly tunnelDataStore: TunnelDataStore,
    private readonly tunnelNavigationStore: TunnelNavigationStore
  ) {
    super(workbenchService);
    this.preview = configurators2DComponent.preview as IWorkbenchPreview;
  }

  get kitchenPlans() {
    return this.kitchenMedias.plan2D;
  }

  get kitchenThumbnail() {
    return this.kitchenMedias.thumbnail[0]; //petit format
  }

  get isTunnel(): boolean {
    return this.tunnelNavigationStore?.isTunnelValue;
  }

  loadKitchenCarouselMedias() {
    let carouselMedias: HBMMedia[] = [];
    carouselMedias.push(this.kitchenMedias.thumbnail[1]); //grand format;
    carouselMedias = carouselMedias.concat(this.kitchenMedias.plan2D);
    this.kitchenCarouselMedias = carouselMedias;
  }

  setKitchenPlanVisible(kitchenPlanVisible: boolean) {
    this.loadKitchenCarouselMedias();
    this.kitchenPlanVisible = kitchenPlanVisible;
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.service.getShapes().then(shapes => (this.shapes = shapes));

    this.preview.update();

    this.preview.update();

    this.subscriptions.push(
      this.preview.onPlanSelect.subscribe(id => {
        this.displayedPlanIndex = id;
      })
    );

    this.subscriptions.push(
      this.preview.onGroupCreate.subscribe(plansIds => {
        setTimeout(() => {
          this.workbenchService.createOrUpdateConfigurationGroup(plansIds);
        });
      })
    );

    this.subscriptions.push(
      this.preview.onGroupDelete.subscribe(plansIds => {
        setTimeout(() => {
          this.workbenchService.updateOrDeleteConfigurationGroup(plansIds);
        });
      })
    );

    this.subscriptions.push(
      this.preview.onSuperposition.subscribe(hasCollision => {
        setTimeout(() => {
          // Just trigger change detection by running in timeout
          // The button state depends on configuration.hasSuperposition which is updated by reference
        });
      })
    );

    this.preview.actionByConfiguratorStep();

    if (this.preview.configurationIsNotLoad()) {
      this.preview.loadExistingConfiguration();
    } else if (
      this.configuration.workbenchConfigurationPlans == null ||
      this.configuration.workbenchConfigurationPlans.length === 0
    ) {
      this.addConfigurationPlan();
    }

    this.preview.previewSelectPlanById(1);
    //Dans le cas où on est dans un tunnel de kitchen, il faut charger les plan 2D pour affichage
    if (this.isTunnel) {
      switch (this.mainContext) {
        case CONFIG_CONTEXT.KITCHEN:
          this.initKitchenSubscriptions();
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions?.forEach(subscription => subscription.unsubscribe());
  }

  private initSubscriptions() {
    this.subscriptions.push(
      this.tunnelDataStore.getMainContext().subscribe(mainContext => {
        if (mainContext) this.mainContext = mainContext;
      })
    );
  }

  private initKitchenSubscriptions() {
    this.subscriptions.push(
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.kitchenMedias = (configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData).medias;
        }
      })
    );
  }

  /**
   * Ajoute un plan de travail à la config.
   */
  addConfigurationPlan(): void {
    this.configuration.workbenchConfigurationPlans ??= [];

    let displayOrder = (this.configuration.workbenchConfigurationPlans?.length ?? 0) + 1;

    const newPlan = new ConfigurationPlan(displayOrder);

    // Initialize with default shape to prevent preview crash
    if (this.shapes) {
      const defaultShape = this.shapes.find(s => s.shapeType === ShapeTypeEnum.Rectangle);
      if (defaultShape) {
        newPlan.shape = defaultShape;
        newPlan.shapeId = defaultShape.id;
        newPlan.width = WIDTH_DEFAULT_RECT; // Default width
        newPlan.length = LENGTH_DEFAULT_RECT; // Default length
      }
    }

    this.configuration.workbenchConfigurationPlans.push(newPlan);
    // on affiche la dernière box qu'on crée
    this.displayedPlanIndex = displayOrder;
    this.preview.previewSelectPlanById(displayOrder);

    // Trigger update to ensure 3D auto-placement logic runs
    setTimeout(() => {
      this.preview.update();
    });
  }

  async toggleCollapse(subStep: number) {
    this.subStep = subStep;
  }

  /**
   * Affiche le plan en train d'être traité.
   * @param isVisible
   * @param index
   */
  toggleCollapseSection(isVisible: boolean, index: number) {
    if (isVisible) {
      this.displayedPlanIndex = index;
    } else {
      this.displayedPlanIndex = 0;
    }
    this.preview.previewSelectPlanById(index);
  }

  isNextButtonActive(): boolean {
    let groupsExistAndAreValids = true;
    if (
      this.configuration.workbenchConfigurationGroups?.length != undefined &&
      this.configuration.workbenchConfigurationGroups?.length > 0
    ) {
      groupsExistAndAreValids = this.configuration.workbenchConfigurationGroups.every(group => group.isValid());
    }

    return (
      this.configuration.workbenchConfigurationPlans?.length > 0 &&
      this.configuration.workbenchConfigurationPlans?.every(plan => isPlanDimensionsValid(plan)) &&
      !this.configuration.hasSuperposition &&
      groupsExistAndAreValids
    );
  }

  checkPageChangeCapability(): boolean {
    return true;
  }

  /**
   * Retire un plan
   * @param index
   */
  deleteSection() {
    this.displayedPlanIndex = this.preview.deletePlan() || 0;
    this.workbenchService.configuration.workbenchConfigurationPlans.splice(
      this.workbenchService.configuration.workbenchConfigurationPlans.findIndex(
        x => x.displayOrder == this.displayedPlanIndex
      ),
      1
    );
    this.onSectionSelected.emit(false);
  }
}
